/**
 * The map's scenery: the mountains and the three ground markers, placed as
 * sprites at setup and shown only while the camera can see them. Legacy kept
 * this in Mobs.js and culled through coordinate buckets; a Flatbush index does
 * that here. The mountains also pull nearby shadows in, through mountainFactor.
 */
import Flatbush from 'flatbush';
import type { Sprite } from 'pixi.js';
import { clock } from './Clock';
import { DISPLAY_DEFAULTS } from './Config';
import { OVERDRAW } from './Constants';
import { type Doodad, groundMarkers, mountains } from './doodads/DoodadData';
import { getCamera as graphicsGetCamera, inScreen as graphicsInScreen, viewport } from './Graphics';
import { GAME_STATE_CODES, session } from './Session';
import { settings } from './Settings';
import { init as texturesInit } from './Textures';
import { clamp, distance, distFastCheckFloat } from './Tools';
import type { PropOverrides } from './types/Textures';
import Vector from './Vector';

/** The doodad textures are 512 px square, so a doodad reaches this far times its scale. */
const REACH = 256;

/** Slack around the view when asking the index, as legacy's bucket scan had. */
const CULL_MARGIN = 512;

/** Terrain tint color used by the original hitshapes theme. */
const HITSHAPES_MOUNTAIN_TINT = 0xa18d72;

/** Every doodad, the mountains first and the ground markers after, as legacy merged them. */
const all: readonly Doodad[] = [...mountains, ...groundMarkers];

/** How far from its centre a doodad can touch the view, overdraw included. */
function cullRadius(doodad: Doodad): number {
  return REACH * doodad.scale + OVERDRAW;
}

/** The index over every doodad's reach box, built once from the static data. */
const index = new Flatbush(all.length);
for (const doodad of all) {
  const radius = cullRadius(doodad);
  index.add(doodad.x - radius, doodad.y - radius, doodad.x + radius, doodad.y + radius);
}
index.finish();

/** A doodad's sprite and the frame the camera last saw it. */
interface PlacedDoodad {
  readonly doodad: Doodad;
  readonly sprite: Sprite;
  seenFrame: number;
}

/** One per doodad, in the data's order, so an index hit maps straight to it. Filled by setup. */
const placed: PlacedDoodad[] = [];

/** The doodads on screen, in the order they came into view. */
const visible: PlacedDoodad[] = [];

/** Scratch vector for the screen check, reused each frame. */
const scratch = new Vector(0, 0);

/** The visible and total counts, for the debug overlay. */
export function count(): [number, number] {
  return [visible.length, all.length];
}

/** Sets or removes the terrain tint on every mountain sprite (hitshapes theme). */
export function setMountainTint(enabled: boolean): void {
  for (const { doodad, sprite } of placed) {
    if (doodad.isMountain) {
      sprite.tint = enabled ? HITSHAPES_MOUNTAIN_TINT : 0xffffff;
    }
  }
}

/** Places every doodad's sprite, hidden; the textures must be loaded. */
export function setup(): void {
  const hitshapes = (settings.display?.theme ?? DISPLAY_DEFAULTS.theme) === 'hitshapes';
  const mountainTint = settings.display?.mountainTint ?? DISPLAY_DEFAULTS.mountainTint;
  for (const doodad of all) {
    placed.push({ doodad, sprite: place(doodad, hitshapes, mountainTint), seenFrame: -1 });
  }
}

/** Creates a doodad's sprite with its placement and look, through the overrides init applies at creation. */
function place(doodad: Doodad, hitshapes: boolean, mountainTint: boolean): Sprite {
  const overrides: PropOverrides = {
    position: [doodad.x, doodad.y],
    scale: doodad.scale,
    visible: false,
  };
  if (doodad.rotation !== 0) {
    overrides.rotation = doodad.rotation;
  }
  if (doodad.alpha != null) {
    overrides.alpha = doodad.alpha;
  }
  if (hitshapes) {
    // The hitshapes theme tints every mountain the same, when the setting asks for it
    if (doodad.isMountain && mountainTint) {
      overrides.tint = HITSHAPES_MOUNTAIN_TINT;
    }
  } else if (doodad.tint != null) {
    overrides.tint = doodad.tint;
  }
  return texturesInit(doodad.textureName, overrides);
}

/**
 * How close a point is to the nearest mountain on screen, as a factor from
 * 0.2 at the centre to 1 clear of it. Shadows move in by it, so a plane over
 * a mountain reads as nearer the ground. Legacy's formula, over the mountains
 * in view, exact for every caller.
 */
export function mountainFactor(position: Vector): number {
  let nearest = 2;
  for (const entry of visible) {
    const doodad = entry.doodad;
    if (!doodad.isMountain) {
      continue;
    }
    const reach = REACH * doodad.scale;
    if (!distFastCheckFloat(position.x, position.y, doodad.x, doodad.y, reach)) {
      continue;
    }
    const normalized = distance(position.x, position.y, doodad.x, doodad.y) / reach;
    if (normalized < nearest) {
      nearest = normalized;
    }
  }
  return clamp(3.333 * (nearest - 0.5), 0.2, 1);
}

/**
 * Shows the doodads the camera can see and hides the rest: the index narrows
 * the search to the reach boxes around the view, and the screen check decides.
 * Graphics.update runs this every frame; legacy ran it only when the
 * background redrew, so its doodads could trail the camera by the overdraw.
 */
export function update(): void {
  const camera = graphicsGetCamera();
  const marginX = CULL_MARGIN + viewport.halfWidth / viewport.scale;
  const marginY = CULL_MARGIN + viewport.halfHeight / viewport.scale;
  const frame = clock.frames;
  const titleScreen = session.state === GAME_STATE_CODES.LOGIN;

  for (const i of index.search(camera.x - marginX, camera.y - marginY, camera.x + marginX, camera.y + marginY)) {
    const entry = placed[i];
    if (entry === undefined) {
      continue;
    }
    const doodad = entry.doodad;
    // The title screen shows the mountains only, not the ground markers
    if (titleScreen && !doodad.isMountain) {
      continue;
    }
    scratch.x = doodad.x;
    scratch.y = doodad.y;
    if (!graphicsInScreen(scratch, cullRadius(doodad))) {
      continue;
    }
    entry.seenFrame = frame;
    if (!entry.sprite.visible) {
      entry.sprite.visible = true;
      visible.push(entry);
    }
  }

  // Hide what the camera left behind and keep the rest in place
  let kept = 0;
  for (const entry of visible) {
    if (entry.seenFrame === frame) {
      visible[kept++] = entry;
    } else {
      entry.sprite.visible = false;
    }
  }
  visible.length = kept;
}
